import React, { useEffect, useRef, useState } from 'react';

import { AutoPauseVideo } from '../Generic/AutoPauseVideo';
import PageSection from '../PageSections/PageSection';
import ContentfulImage from './ContentfulImage';
import RichTextConverter from './RichText/RichTextConverter';
import * as styles from './TabbedCustomerStories.module.scss';

const Stat = ({ stat }) => (
  <div className={styles.stat}>
    <span className={styles.bigStat}>{stat.title}</span>
    <span>{stat.subtitle}</span>
  </div>
);

const ANIMATION_SPEED = 5000;
const ANIMATION_KEYFRAMES = [
  {
    transform: 'scaleX(0)',
  },
  { transform: 'none' },
];
const ANIMATION_SETTINGS = { fill: 'forwards', easing: 'linear', duration: ANIMATION_SPEED };

export default function TabbedCustomerStories({ section }) {
  const [activeTab, setActiveTab] = useState(0);
  const [canAnimate, setCanAnimate] = useState(true);
  const container = useRef();
  const progressBar = useRef();
  const animationRef = useRef();

  const colorTheme = section.colorTheme?.themeName;

  const backgroundColors = { 'Light Lilac': styles.lilacLight, 'Mint Condition': styles.mintCondition };
  const stories = section.content?.filter((story) => story.__typename === 'ContentfulSectionContentSection');

  // play progress animation
  useEffect(() => {
    if (canAnimate) {
      animationRef.current = progressBar.current.animate(ANIMATION_KEYFRAMES, ANIMATION_SETTINGS);

      const timeout = setTimeout(() => {
        if (activeTab + 2 > stories.length) {
          setActiveTab(0);
        } else {
          setActiveTab((value) => value + 1);
        }
      }, [ANIMATION_SPEED]);

      return () => clearTimeout(timeout);
    } else {
      animationRef.current?.pause();
    }
  }, [canAnimate, activeTab, stories.length]);

  return (
    <div className={styles.container} ref={container} onClick={() => setCanAnimate(false)}>
      <svg ref={progressBar} className={styles.progressBar}>
        {canAnimate && <rect height="100%" width="100%" />}
      </svg>
      <PageSection
        slim
        className={[styles.section, backgroundColors[colorTheme || section.backgroundColor]].join(' ')}
        data-analytics-section={'tabbed-customer-stories'}
      >
        <div className={styles.tabs} style={{ gridTemplateColumns: `repeat(${stories.length}, 1fr)` }}>
          {stories?.map((story, i) => (
            <button
              key={i}
              className={[styles.logo].join(' ')}
              data-analytics-component={'customer-logo-tab'}
              data-analytics-id={story.logo.title}
              onClick={() => setActiveTab(i)}
            >
              <ContentfulImage imageFragment={story.logo} />
            </button>
          ))}
          <svg className={styles.activeBar}>
            <rect
              className={styles.activeLine}
              width={`${100 / stories.length}%`}
              height="100%"
              style={{ transform: `translateX(${(100 / stories.length) * activeTab}%)` }}
            />
          </svg>
        </div>
        <div className={styles.stories}>
          {stories?.map((story, i) => {
            return (
              <div
                key={i}
                className={[styles.story].join(' ')}
                style={{ transform: `translateX(calc(${-100 * activeTab}% + ${-1.5 * activeTab}rem))` }}
                data-analytics-component={'customer-story'}
                data-analytics-id={story.title}
              >
                <div>
                  <h2>{story.title}</h2>
                  <RichTextConverter copy={story.copy} />
                  <div className={styles.desktopStats}>
                    {story.contentSection?.content?.map((stat, i) => {
                      return <Stat key={i} stat={stat} />;
                    })}
                  </div>
                </div>
                <div className={styles.visual}>
                  {story.wistiaUrl ? (
                    <AutoPauseVideo
                      controls={true}
                      src={story.wistiaUrl}
                      classes={[styles.videoPlayer]}
                      customThumbnail={story.backgroundImage ? story.backgroundImage.file.url : null}
                    />
                  ) : (
                    <ContentfulImage lazy={true} imageFragment={story.mainImage} />
                  )}
                </div>
                <div className={styles.mobileStats}>
                  {story.contentSection?.content?.map((stat, i) => {
                    return <Stat key={i} stat={stat} />;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </PageSection>
    </div>
  );
}
